import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Box, Grid, Typography } from "@mui/material";
import React from "react";
import { MultilineTextInput } from '../../../../components/FormComponents/MultilineTextInput';
import { TextInput } from '../../../../components/FormComponents/TextInput';
import { YesNoCheckboxGroup } from '../../../../components/FormComponents/YesNoCheckboxGroup';
import { IEmployeeInfo, IPaidMedicalLeaveRequestData } from '../../PaidMedicalLeaveRequestData';
import { defaultDisclaimer, IDisclaimer } from '../../PaidMedicalLeaveRequestModels';
import { Disclaimer } from '../Disclaimer';
import { bulletParagraphStyle, doubleUnderlineStyle } from './styled';

interface ILeaveDetailsProps {
  data: IPaidMedicalLeaveRequestData;
  onChangeField: <K extends keyof IEmployeeInfo>(
    label: K,
    value: IEmployeeInfo[K]
  ) => void;
  disclaimer: IDisclaimer,
  onShowDisclaimer: (dislaimer: IDisclaimer) => void;
  error: Record<string, JSX.Element | string>;
  blurredFields: Partial<Record<keyof IEmployeeInfo, boolean>>
}

export const LeaveDetails: React.FC<ILeaveDetailsProps> = ({
  disclaimer,
  onShowDisclaimer,
  data,
  onChangeField,
  error,
  blurredFields
}) => (
  <Box sx={{ p: { xs: 0, md: 2 } }}>
    {disclaimer && (!!disclaimer?.title || !!disclaimer.text?.length) && <Disclaimer
      onClick={() => onShowDisclaimer(defaultDisclaimer)}
      title={disclaimer?.title || ''}
      text={disclaimer?.text || []}
      bulletParagraph={disclaimer.bulletParagraph}
    />}
    <Grid container spacing={2}>
      <Grid item xs={6} sx={{ p: 0 }}>
        <Box
          sx={{ display: 'flex', cursor: 'pointer' }}
          onClick={() => onShowDisclaimer({ title: 'Medical Leave', text: ['Employees may take up to ten (10) days of Medical Leave each year.'] })}
        >
          <HelpOutlineIcon sx={{ color: 'corporateBlue', mr: 1 }} />
          <Typography sx={{ color: 'corporateBlue', textDecoration: 'underline' }}>Medical Leave</Typography>
        </Box>
      </Grid>
      <Grid item xs={6} sm={6} sx={{ p: 0 }}>
        <Box
          sx={{ display: 'flex', cursor: 'pointer' }}
          onClick={() => onShowDisclaimer({
            title: 'Eligibility Criteria',
            text: ['Minimum length of service for paid leave is 30 days of continuous employment with the employer to receive three (3) days of paid medical leave, and an additional 30 days of continuous employment to receive one (1) additional day of paid medical leave, up to a maximum of ten (10) days per calendar year',
              'Employees will be able to carry forward any unused leave credits earned in the previous year, but the total paid medical leave will remain at a maximum of ten (10) days per year',
              <Typography sx={bulletParagraphStyle}>Any paid medical leave taken prior to December 1, <Typography component='span' sx={doubleUnderlineStyle}>2022</Typography> cannot count towards an employee’s future accrual of paid Medical Leave under the Code</Typography>,
              'The Company will review whether the employee would have been dispatched or scheduled that day, based on their training and seniority, had they been dispatched or otherwise scheduled to work, to determine whether the employee is entitled to the days requested for Medical Leave'
            ] as Array<string | JSX.Element>,
            bulletParagraph: true
          })}
        >
          <HelpOutlineIcon sx={{ color: 'corporateBlue', mr: 1 }} />
          <Typography sx={{ color: 'corporateBlue', textDecoration: 'underline' }}>Eligibility Criteria</Typography>
        </Box>
      </Grid>
    </Grid>
    <Grid container spacing={2} sx={{ mt: 2 }}>
      <Grid item xs={12} sx={{ p: 0 }}>
        <YesNoCheckboxGroup
          label="Have you been approved for Weekly Indemnity?"
          fieldKey="WIApproved"
          value={data?.employeeInfo.WIApproved ?? null}
          onChange={(key, value) => onChangeField('WIApproved', value)}
          required
          error={blurredFields.WIApproved ? error.WIApproved : ''}
        />
      </Grid>
      <Grid item xs={12} sx={{ p: 0 }}>
        <YesNoCheckboxGroup
          label="Have you been approved for an unpaid Medical Leave?"
          fieldKey="unpaidMedicalLeaveApproved"
          value={data?.employeeInfo.unpaidMedicalLeaveApproved ?? null}
          onChange={(key, value) => onChangeField('unpaidMedicalLeaveApproved', value)}
          required
          error={blurredFields.unpaidMedicalLeaveApproved ? error.unpaidMedicalLeaveApproved : ''}
        />
      </Grid>
      <Grid item xs={12} sx={{ p: 0 }}>
        <MultilineTextInput
          label="Written Statement on Reason for Leave:"
          placeholder="Please indicate the reason in support of your medical leave application."
          value={data?.employeeInfo.reasonForLeave || ""}
          onChange={(value) => onChangeField("reasonForLeave", value)}
          maxLength={200}
          required
          error={blurredFields.reasonForLeave ? error.reasonForLeave : ''}
        />
      </Grid>
      <Grid item xs={12} sx={{ p: 0 }}>
        <YesNoCheckboxGroup
          label="Are you regular workforce?"
          fieldKey="regularWorkforce"
          value={data?.employeeInfo.regularWorkforce ?? null}
          onChange={(key, value) => onChangeField('regularWorkforce', value)}
          required
          error={blurredFields.regularWorkforce ? error.regularWorkforce : ''}
        />
        <TextInput
          placeholder="Detail"
          value={data?.employeeInfo.regularWorkforceDetail || ""}
          onChange={(value) => onChangeField("regularWorkforceDetail", value)}
          maxLength={200}
        />
      </Grid>
      <Grid item xs={12} sx={{ p: 0 }}>
        <YesNoCheckboxGroup
          label="Are you eligible to work?"
          fieldKey="eligibleToWork"
          value={data?.employeeInfo.eligibleToWork ?? null}
          onChange={(key, value) => onChangeField('eligibleToWork', value)}
          disclaimer={{
            title: 'Eligible to Work?',
            text: ['There is not a discipline-related leave, unpaid leave or cannot work for other reasons.'],
            bulletParagraph: true
          }}
          onShowDisclaimer={onShowDisclaimer}
          required
          error={blurredFields.eligibleToWork ? error.eligibleToWork : ''}
        />
        <TextInput
          placeholder="Detail"
          value={data?.employeeInfo.eligibleToWorkDetail || ""}
          onChange={(value) => onChangeField("eligibleToWorkDetail", value)}
          maxLength={200}
        />
      </Grid>
      <Grid item xs={12} sx={{ p: 0 }}>
        <YesNoCheckboxGroup
          disclaimer={{
            title: 'Work Restrictions?',
            text: ['This question is asking if you have restriction non-related to the leave requested in this form.'],
            bulletParagraph: true
          }}
          onShowDisclaimer={onShowDisclaimer}
          label="Is there a restriction which would prevent the employee from being scheduled or working the day and shift requested for medical leave?"
          fieldKey="workRestriction"
          value={data?.employeeInfo.workRestriction ?? null}
          onChange={(key, value) => onChangeField('workRestriction', value)}
          required
          error={blurredFields.workRestriction ? error.workRestriction : ''}
        />
        <TextInput
          placeholder="Detail"
          value={data?.employeeInfo.workRestrictionDetail || ""}
          onChange={(value) => onChangeField("workRestrictionDetail", value)}
          maxLength={200}
        />
      </Grid>
      <Grid item xs={12} sx={{ p: 0 }}>
        <YesNoCheckboxGroup
          label="Would the employee have been dispatched to work or otherwise scheduled to work based on training and seniority?"
          fieldKey="dispatchedToWork"
          value={data?.employeeInfo.dispatchedToWork ?? null}
          onChange={(key, value) => onChangeField('dispatchedToWork', value)}
          required
          error={blurredFields.dispatchedToWork ? error.dispatchedToWork : ''}
        />
        <TextInput
          placeholder="Detail"
          value={data?.employeeInfo.dispatchedToWorkDetail || ""}
          onChange={(value) => onChangeField("dispatchedToWorkDetail", value)}
          maxLength={200}
          required
        />
      </Grid>
    </Grid>
  </Box>
);
